/**
 * JiraExportDialog — Confirmation dialog for exporting a board to
 * Jira-compatible format (ZIP with CSV + attachments).
 *
 * States: idle → exporting → done / error
 * On success, triggers download via presigned S3 URL.
 *
 * Requirements: 6.2–6.6
 */

import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { post } from '../../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface JiraExportDialogProps {
  readonly boardId: string;
  readonly boardTitle: string;
  readonly open: boolean;
  readonly onClose: () => void;
}

type ExportState = 'idle' | 'exporting' | 'done' | 'error';

interface ExportResponse {
  readonly data: {
    readonly downloadUrl: string;
    readonly warnings?: string[];
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function JiraExportDialog({
  boardId,
  boardTitle,
  open,
  onClose,
}: JiraExportDialogProps): React.ReactElement {
  const [state, setState] = useState<ExportState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  const handleExport = useCallback(async () => {
    setState('exporting');
    setErrorMessage(null);
    setWarnings([]);

    try {
      const response = await post<ExportResponse>(
        `/boards/${boardId}/export/jira`,
      );

      setState('done');

      if (response.data.warnings) {
        setWarnings(response.data.warnings);
      }

      // Trigger download via presigned URL
      window.location.href = response.data.downloadUrl;
    } catch (error: unknown) {
      setState('error');
      const message = error instanceof Error
        ? error.message
        : 'Export failed. Please try again.';
      setErrorMessage(message);
    }
  }, [boardId]);

  const handleClose = useCallback(() => {
    // Reset state when closing
    setState('idle');
    setErrorMessage(null);
    setWarnings([]);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent aria-labelledby="jira-export-title">
        <DialogHeader>
          <DialogTitle id="jira-export-title">Export to Jira</DialogTitle>
          <DialogDescription>
            Export "{boardTitle}" as a Jira-compatible archive for import into Jira Data Center.
          </DialogDescription>
        </DialogHeader>

        {state === 'idle' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The export will include:
            </p>
            <ul className="list-disc pl-5 text-sm text-muted-foreground space-y-1">
              <li>All cards with descriptions, labels, and due dates</li>
              <li>Comments with author attribution</li>
              <li>Checklists converted to Jira subtasks</li>
              <li>File attachments for offline import</li>
              <li>Custom field values</li>
              <li>Import instructions for the Jira admin</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-2">
              The archive is designed for air-gapped Jira Data Center instances — no internet connection required for import.
            </p>
          </div>
        )}

        {state === 'exporting' && (
          <div className="flex items-center gap-3 py-4" role="status" aria-live="polite">
            <svg
              className="h-5 w-5 animate-spin text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm text-muted-foreground">
              Preparing Jira export... This may take a moment for large boards.
            </span>
          </div>
        )}

        {state === 'done' && (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Export complete. Download should start automatically.</span>
            </div>
            {warnings.length > 0 && (
              <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3">
                <p className="text-xs font-medium text-yellow-800 mb-1">Warnings:</p>
                <ul className="list-disc pl-4 text-xs text-yellow-700 space-y-0.5">
                  {warnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {state === 'error' && (
          <div className="rounded-md bg-red-50 border border-red-200 p-3" role="alert">
            <p className="text-sm text-red-700">
              {errorMessage ?? 'An unexpected error occurred.'}
            </p>
          </div>
        )}

        <DialogFooter>
          {state === 'idle' && (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExport}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Download Export
              </button>
            </>
          )}
          {state === 'exporting' && (
            <button
              type="button"
              disabled
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
            >
              Exporting...
            </button>
          )}
          {(state === 'done' || state === 'error') && (
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              Close
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
